import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useManualQuery } from 'graphql-hooks'
import { Avatar, Input, Table } from 'antd'
import { SearchOutlined } from '@ant-design/icons'

import * as logger from '../../../../../services/logger'
import { getAllUsersSlim } from '../../../../../graphql/custom-queries'
import { Button } from '../../../../../design-system/Button'
import { FormModal } from '../../../../../design-system/FormModal'
import { EmptyState } from '../../../../../design-system/EmptyState'
import { Stack } from '../../../../../design-system/Stack'
import { Text } from '../../../../../design-system/Text'
import { useBulkUserMove } from '../../../../Users/UsersPage/hooks/useBulkUserMove'

interface ISlimUser {
  id: string
  name: string
  status: string
  email: string
  locationId: string
  teamId: string
}

interface ICandidateRow extends ISlimUser {
  teamName: string
  locationName: string
}

/** Comfortably under DynamoDB's 1MB page, so a page boundary is the exception rather than the rule. */
const USERS_PAGE_SIZE = 500

/** A loop limit for a server that keeps handing back a token; 50 pages is far past any workspace. */
const MAX_PAGES = 50

export interface IAddUsersFromElsewhereModalProps {
  locationId: string
  locationName: string
  /** The candidate rows carry ids only, so the department and location columns resolve through these. */
  namesById: { locations: Record<string, string>; teams: Record<string, string> }
  onClose: () => void
}

const getInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('')

export const AddUsersFromElsewhereModal = (props: IAddUsersFromElsewhereModalProps): React.ReactElement => {
  const { locationId, locationName, namesById, onClose } = props
  const { formatMessage } = useIntl()
  const bulkMove = useBulkUserMove()

  const [candidates, setCandidates] = useState<ISlimUser[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [getAllUsersSlimQuery] = useManualQuery<{ getUsersFiltered: { users: ISlimUser[]; nextToken: string | null } }, { status: string; limit: number; nextToken?: string }>(
    getAllUsersSlim
  )

  useEffect(() => {
    let cancelled = false

    // Every page, not just the first. DynamoDB caps a page at 1MB of scanned items whatever Limit
    // says, so a large workspace comes back truncated with a nextToken - and someone missing from
    // the list is unaddable with nothing on screen to say why.
    const loadCandidates = async () => {
      const found: ISlimUser[] = []
      let nextToken: string | undefined
      let pages = 0

      do {
        const response = await getAllUsersSlimQuery({ variables: { status: 'ACTIVE', limit: USERS_PAGE_SIZE, nextToken } })
        const page = response.data?.getUsersFiltered
        found.push(...(page?.users ?? []))
        nextToken = page?.nextToken ?? undefined
        pages += 1
      } while (nextToken && pages < MAX_PAGES && !cancelled)

      if (!cancelled) {
        // The API filters by location inclusion only, so "everyone else" is a client-side exclusion.
        setCandidates(found.filter((user) => user.locationId !== locationId))
      }
    }

    loadCandidates()
      .catch((error) => logger.warning({ errorType: 'ERROR FETCH ADD USER CANDIDATES', error }))
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [locationId])

  const rows: ICandidateRow[] = useMemo(
    () =>
      candidates.map((user) => ({
        ...user,
        teamName: namesById.teams[user.teamId] ?? '',
        locationName: namesById.locations[user.locationId] ?? '',
      })),
    [candidates, namesById]
  )

  // The whole list is already in memory, so this filters without a round trip or a debounce.
  const visibleRows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) {
      return rows
    }
    return rows.filter((row) => `${row.name} ${row.teamName} ${row.locationName}`.toLowerCase().includes(needle))
  }, [rows, search])

  const submit = async () => {
    setIsSubmitting(true)
    try {
      const { failedIds } = await bulkMove({
        eventType: 'LOCATION_USER_MOVED',
        targetId: locationId,
        targetName: locationName,
        users: selectedIds.map((id) => ({ id })),
        errorMessageId: 'locationGeneral.addUsersError',
      })
      // Closing on a partial failure puts the error toast over a dismissed modal and throws away
      // the selection. Keep the people who did not move selected, so retrying is one click.
      if (failedIds.length > 0) {
        setSelectedIds(failedIds)
        return
      }
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  const columns = [
    {
      title: formatMessage({ id: 'app.name' }),
      dataIndex: 'name',
      render: (_: string, row: ICandidateRow) => (
        <Stack direction="horizontal" align="center" gap="xs">
          {/* Initials, not UserAvatar: getAllUsersSlim carries no imageUrl, so that would render a
              row of identical silhouettes. */}
          <Avatar size={32}>{getInitials(row.name)}</Avatar>
          <span>{row.name}</span>
        </Stack>
      ),
    },
    { title: formatMessage({ id: 'app.department' }), dataIndex: 'teamName' },
    { title: formatMessage({ id: 'app.location' }), dataIndex: 'locationName' },
  ]

  return (
    <FormModal
      open
      width="large"
      title={formatMessage({ id: 'users.addUsers' })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{formatMessage({ id: 'app.cancel' })}</Button>
          <Button variant="primary" loading={isSubmitting} disabled={selectedIds.length === 0} onClick={submit}>
            {formatMessage({ id: 'locationGeneral.addUsersConfirm' }, { count: selectedIds.length })}
          </Button>
        </>
      }
    >
      {!isLoading && candidates.length === 0 ? (
        <EmptyState description={formatMessage({ id: 'locationGeneral.addNobodyElsewhere' })} />
      ) : (
        <>
          <Text tone="secondary" size="sm" block>
            {formatMessage({ id: 'locationGeneral.addFromElsewhere' })}
          </Text>
          {/* The line above explains the move; this one explains what it costs. The old location
              form warned on any change to the user list, and moving people is that change. */}
          <Text tone="secondary" size="sm" block>
            {formatMessage({ id: 'components.locationForm.warningInitialRolloverUsers' })}
          </Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={formatMessage({ id: 'users.search' })}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ margin: '16px 0 12px' }}
          />
          <Table
            rowKey="id"
            size="middle"
            loading={isLoading}
            columns={columns}
            dataSource={visibleRows}
            pagination={false}
            scroll={{ y: 360 }}
            // preserveSelectedRowKeys: searching narrows dataSource, and without it antd drops the
            // selection of anyone the search hides.
            rowSelection={{
              type: 'checkbox',
              preserveSelectedRowKeys: true,
              selectedRowKeys: selectedIds,
              onChange: (keys) => setSelectedIds(keys as string[]),
            }}
          />
        </>
      )}
    </FormModal>
  )
}
